import { Col, Text } from 'components';
import {
  createProfanityTerm,
  getProfanityTerms,
  removeProfanityTerm,
  theme,
  updateProfanityTerm
} from 'lib';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import KeywordModal from './KeywordModal';
import KeywordsTable from './KeywordsTable';
import Pagination from './Pagination';
import { AccessDenied, ErrorState } from './ui';

const DEFAULT_FILTERS = { active: 'all', search: '' };
const FILTER_KEYS = Object.keys(DEFAULT_FILTERS);
const DEFAULT_LIMIT = 50;
// Lives inside the "Spam" top-level tab, on its "keywords" sub-tab.
const TAB = 'spam';
const SUB_TAB = 'keywords';
// Always scoped to the High profanity level — this tab is a "hard block a
// business-rule word" shortcut over the Profanity term API, not a new list.
const KEYWORD_LEVEL = 'high';

const selectCls =
  'h-[34px] border border-[#D2D8DF] rounded-lg px-2.5 text-[13px] text-[#353E4A] bg-white focus:outline-none focus:ring-2 focus:ring-[#FA3F58]/20';
const inputCls =
  'h-[34px] border border-[#D2D8DF] rounded-lg px-3 text-[13px] text-[#353E4A] bg-white focus:outline-none focus:ring-2 focus:ring-[#FA3F58]/20';
const labelCls = 'text-[10px] font-semibold text-[#97A3B5] uppercase tracking-wide';

/** Blocked Keywords: instant-block words that aren't profanity (crypto, bitcoin, etc). */
const KeywordsTab = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const applied = FILTER_KEYS.reduce((acc, key) => {
    acc[key] = searchParams.get(key) || DEFAULT_FILTERS[key];
    return acc;
  }, {});
  const page = Number(searchParams.get('page')) || 1;
  const limit = Number(searchParams.get('limit')) || DEFAULT_LIMIT;
  const appliedKey = searchParams.toString();

  const [searchInput, setSearchInput] = useState(applied.search);
  const searchTimer = useRef(null);

  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const reqIdRef = useRef(0);

  const fetchKeywords = async (f, p, lim) => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    const res = await getProfanityTerms({ ...f, level: KEYWORD_LEVEL, page: p, limit: lim });
    if (reqId !== reqIdRef.current) return;

    if (res?.status === 403) {
      setForbidden(true);
      setLoading(false);
      return;
    }
    if (res?.err || !res?.data) {
      setError(res?.err || 'Could not load blocked keywords.');
      setLoading(false);
      return;
    }
    setItems(res.data.items || []);
    setPagination(res.pagination || null);
    setLoading(false);
  };

  useEffect(() => {
    fetchKeywords(applied, page, limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedKey]);

  useEffect(() => {
    setSearchInput(applied.search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied.search]);

  useEffect(() => () => searchTimer.current && clearTimeout(searchTimer.current), []);

  const writeParams = (updates) => {
    const next = { tab: TAB, sub: SUB_TAB };
    searchParams.forEach((v, k) => {
      next[k] = v;
    });
    Object.entries(updates).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '' || v === 'all') delete next[k];
      else next[k] = String(v);
    });
    setSearchParams(next, { replace: true });
  };

  const handleFilterChange = (key, value) => writeParams({ [key]: value, page: 1 });

  const handleSearchChange = (value) => {
    setSearchInput(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => writeParams({ search: value, page: 1 }), 400);
  };

  const handleClear = () => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    setSearchInput('');
    setSearchParams({ tab: TAB, sub: SUB_TAB });
  };

  const handleToggleActive = async (term) => {
    if (busyId) return;
    setBusyId(term._id);
    const res = await updateProfanityTerm(term._id, { active: !term.active });
    setBusyId(null);

    if (res?.err || !res?.data) {
      dispatch({ type: 'HANDLE_RES', payload: { err: res?.err || 'Update failed.' } });
      return;
    }
    setItems((prev) =>
      prev.map((t) =>
        t._id === term._id ? { ...t, active: res.data.active, updatedAt: res.data.updatedAt } : t
      )
    );
    dispatch({
      type: 'HANDLE_RES',
      payload: { res: `Keyword ${res.data.active ? 'activated' : 'deactivated'}.` }
    });
  };

  const handleDelete = async (term) => {
    if (busyId) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${term.term}" from the blocked keywords?`)) return;
    setBusyId(term._id);
    const res = await removeProfanityTerm(term._id);
    setBusyId(null);

    if (res?.err) {
      dispatch({ type: 'HANDLE_RES', payload: { err: res.err || 'Delete failed.' } });
      return;
    }
    setItems((prev) => prev.filter((t) => t._id !== term._id));
    setPagination((prev) => (prev ? { ...prev, total: Math.max(0, (prev.total || 1) - 1) } : prev));
    dispatch({ type: 'HANDLE_RES', payload: { res: 'Keyword deleted.' } });
  };

  const handleSubmitModal = async ({ term }) => {
    if (submitting) return;
    setSubmitting(true);
    const res = await createProfanityTerm({ term, level: KEYWORD_LEVEL });
    setSubmitting(false);

    if (res?.err || !res?.data) {
      dispatch({ type: 'HANDLE_RES', payload: { err: res?.err || 'Could not add the keyword.' } });
      return;
    }

    dispatch({ type: 'HANDLE_RES', payload: { res: res.msg || 'Keyword added.' } });
    setModalOpen(false);
    fetchKeywords(applied, page, limit);
  };

  const total = pagination?.total ?? 0;
  const totalPages = total > 0 ? Math.max(1, Math.ceil(total / limit)) : 1;

  if (forbidden) return <AccessDenied />;

  return (
    <Col noFlex wid="100%">
      <div className="bg-white border border-[#D2D8DF] rounded-xl p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex flex-col gap-1">
            <label className={labelCls}>Status</label>
            <select
              value={applied.active}
              onChange={(e) => handleFilterChange('active', e.target.value)}
              className={selectCls}
            >
              <option value="all">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className={labelCls}>Search</label>
            <div className="relative flex items-center">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="keyword starts with…"
                className={`${inputCls} pr-7 w-[220px] font-mono`}
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => handleSearchChange('')}
                  className="absolute right-2 text-[#97A3B5] hover:text-[#353E4A] transition-colors"
                  aria-label="Clear search"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 ml-auto">
            <Text c1 col={theme.GREY_60} style={{ fontSize: '12px' }}>
              {total} keywords
            </Text>
            <button
              type="button"
              onClick={handleClear}
              className="h-[34px] px-4 rounded-lg border border-[#D2D8DF] text-[13px] text-[#5F6F86] font-medium hover:bg-[#F0F2F4] transition-colors"
            >
              Clear filters
            </button>
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="h-[34px] px-4 rounded-lg bg-[#FA3F58] text-[13px] text-white font-semibold hover:opacity-90 transition-opacity"
            >
              Add keyword
            </button>
          </div>
        </div>
      </div>

      {error ? (
        <ErrorState message={error} onRetry={() => fetchKeywords(applied, page, limit)} />
      ) : (
        <>
          <KeywordsTable
            rows={items}
            loading={loading}
            busyId={busyId}
            onToggleActive={handleToggleActive}
            onDelete={handleDelete}
          />
          {!loading && total > 0 && (
            <Pagination
              page={page}
              totalPages={totalPages}
              pageSize={limit}
              total={total}
              pageSizeOptions={[25, 50, 100]}
              onPageSizeChange={(size) => writeParams({ limit: size, page: 1 })}
              onPrev={() => writeParams({ page: page - 1 })}
              onNext={() => writeParams({ page: page + 1 })}
              onGoToPage={(p) => writeParams({ page: p })}
            />
          )}
        </>
      )}

      <KeywordModal
        visible={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmitModal}
        submitting={submitting}
      />
    </Col>
  );
};

export default KeywordsTab;
